import {
  animalMaxHealth,
  isAnimalKind,
  MAX_ANIMALS,
  type AnimalSave,
} from "../entities/animals";
import { ARMOR_SLOT_COUNT, HOTBAR_SIZE, INVENTORY_SIZE, type Inventory } from "../player/inventory";
import { MAX_AIR, MAX_HEALTH } from "../player/player";
import { BLOCKS } from "../world/blocks";
import { WORLD_HEIGHT } from "../world/chunk";
import { isItem, maxDurability, stackLimit, type ItemStack } from "../world/items";
import type { GameMode } from "./mode";

/**
 * Versioned world save.
 *
 * The world is reconstructed from its seed, so the bulky part of a save is the
 * handful of player edits rather than generated terrain. `blocks` is a flat
 * `[x, y, z, id, ...]` array, which keeps the JSON compact for a browser-sized
 * build. Container state lives beside those deltas, keyed by block position.
 */

export const SAVE_KEY = "voxelcraft:world-save";
export const SAVE_VERSION = 1 as const;
/** Kept in sync with ChestState; duplicated here so save parsing has no runtime UI imports. */
const CHEST_SLOTS = 27;
/** Kept in sync with FurnaceState. */
const FURNACE_SLOTS = 3;
const MIN_RENDER_DISTANCE = 2;
const MAX_RENDER_DISTANCE = 24;

export interface SavedInventory {
  /** Occupied slots as [index, stack] pairs, so empty slots cost nothing. */
  slots: [number, ItemStack][];
  /** Items parked outside the 36 slots because the pack was full. */
  overflow: ItemStack[];
  /** Equipped armor as [index, stack] pairs (0: helmet, 1: chestplate, 2: leggings, 3: boots). */
  armor?: [number, ItemStack][];
}

export interface SavedChest {
  x: number;
  y: number;
  z: number;
  slots: (ItemStack | null)[];
}

export interface SavedFurnace {
  x: number;
  y: number;
  z: number;
  slots: (ItemStack | null)[];
  progress: number;
  burnRemaining: number;
  burnTotal: number;
}

/** Saved passive animals. Older saves simply omit the field. */
export type SavedAnimal = AnimalSave;

export interface SaveData {
  version: typeof SAVE_VERSION;
  /** Milliseconds since the epoch, reserved for showing save age and diagnostics. */
  savedAt: number;
  world: {
    seed: number;
    mode: GameMode;
    renderDistance: number;
    /** 0..1 through the ten-minute day/night cycle. */
    skyTime: number;
  };
  player: {
    x: number;
    y: number;
    z: number;
    yaw: number;
    pitch: number;
    health: number;
    air: number;
    flying: boolean;
    selectedSlot: number;
    food?: number;
    foodSaturation?: number;
    foodExhaustion?: number;
  };
  spawn: { x: number; y: number; z: number };
  inventories: {
    creative: SavedInventory;
    survival: SavedInventory;
  };
  /** Flat [x, y, z, id, ...] player block edits. */
  blocks: number[];
  chests: SavedChest[];
  furnaces: SavedFurnace[];
  animals: SavedAnimal[];
}

/** The small slice of Storage the save layer needs, which also makes it testable. */
export interface SaveStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function browserStorage(): SaveStorage | null {
  try {
    return (globalThis as { localStorage?: SaveStorage }).localStorage ?? null;
  } catch {
    return null;
  }
}

/** Read and validate the single local save slot. */
export function readSave(storage?: SaveStorage): SaveData | null {
  try {
    const target = storage ?? browserStorage();
    if (!target) return null;
    return parseSave(target.getItem(SAVE_KEY));
  } catch {
    return null;
  }
}

/**
 * True when *something* is stored under the save key, even if this build can't
 * read it. The menu uses this to explain why Continue is missing.
 */
export function hasStoredSave(storage?: SaveStorage): boolean {
  try {
    const target = storage ?? browserStorage();
    return target !== null && target.getItem(SAVE_KEY) !== null;
  } catch {
    return false;
  }
}

/** Write a save, returning false when storage is unavailable or full. */
export function writeSave(save: SaveData, storage?: SaveStorage): boolean {
  try {
    const target = storage ?? browserStorage();
    if (!target) return false;
    target.setItem(SAVE_KEY, JSON.stringify(save));
    return true;
  } catch {
    return false;
  }
}

/** Parse and validate raw JSON; exposed separately so the format can be unit-tested. */
export function parseSave(raw: string | null): SaveData | null {
  if (raw === null) return null;
  try {
    return validateSave(JSON.parse(raw) as unknown);
  } catch {
    return null;
  }
}

export function snapshotInventory(inventory: Inventory): SavedInventory {
  const slots: [number, ItemStack][] = [];
  inventory.slots.forEach((stack, index) => {
    if (stack) slots.push([index, copyStack(stack)]);
  });
  const armor: [number, ItemStack][] = [];
  inventory.armor.forEach((stack, index) => {
    if (stack) armor.push([index, copyStack(stack)]);
  });
  return {
    slots,
    overflow: inventory.overflow.map(copyStack),
    armor,
  };
}

/**
 * Repopulate a pack. Slots are assigned first, then the overflow is appended
 * directly so a full pack restores exactly where it left off rather than being
 * reshuffled by `addStack`.
 */
export function restoreInventory(inventory: Inventory, saved: SavedInventory): void {
  inventory.clear();
  for (const [index, stack] of saved.slots) {
    if (index < 0 || index >= INVENTORY_SIZE) continue;
    inventory.setSlot(index, copyStack(stack));
  }
  for (const stack of saved.overflow) inventory.overflow.push(copyStack(stack));
  if (saved.armor) {
    for (const [index, stack] of saved.armor) {
      if (index < 0 || index >= ARMOR_SLOT_COUNT) continue;
      inventory.setArmorSlot(index, copyStack(stack));
    }
  }
}

function copyStack(stack: ItemStack): ItemStack {
  return stack.durability === undefined
    ? { id: stack.id, count: stack.count }
    : { id: stack.id, count: stack.count, durability: stack.durability };
}

function validateSave(value: unknown): SaveData | null {
  if (!isRecord(value) || value.version !== SAVE_VERSION) return null;
  const savedAt = finite(value.savedAt);
  if (savedAt === null) return null;

  const world = value.world;
  if (!isRecord(world)) return null;
  const seed = integer(world.seed);
  const renderDistance = integer(world.renderDistance);
  const skyTime = finite(world.skyTime);
  if (
    seed === null ||
    (world.mode !== "creative" && world.mode !== "survival") ||
    renderDistance === null ||
    renderDistance < MIN_RENDER_DISTANCE ||
    renderDistance > MAX_RENDER_DISTANCE ||
    skyTime === null ||
    skyTime < 0 ||
    skyTime > 1
  ) {
    return null;
  }

  const player = parsePlayer(value.player);
  if (!player) return null;

  const spawn = parsePosition(value.spawn);
  if (!spawn) return null;

  const inventories = value.inventories;
  if (!isRecord(inventories)) return null;
  const creative = parseInventory(inventories.creative);
  const survival = parseInventory(inventories.survival);
  if (!creative || !survival) return null;

  const blocks = parseBlocks(value.blocks);
  if (!blocks) return null;

  const chests = parseChests(value.chests);
  const furnaces = parseFurnaces(value.furnaces);
  // Backward compatibility: 6B saves predate animals and have no field.
  const animals = parseAnimals(value.animals);
  if (!chests || !furnaces || !animals) return null;

  return {
    version: SAVE_VERSION,
    savedAt,
    world: {
      seed,
      mode: world.mode,
      renderDistance,
      skyTime,
    },
    player,
    spawn,
    inventories: { creative, survival },
    blocks,
    chests,
    furnaces,
    animals,
  };
}

function parsePlayer(value: unknown): SaveData["player"] | null {
  if (!isRecord(value)) return null;
  const x = finite(value.x);
  const y = finite(value.y);
  const z = finite(value.z);
  const yaw = finite(value.yaw);
  const pitch = finite(value.pitch);
  const health = finite(value.health);
  const air = finite(value.air);
  const selectedSlot = integer(value.selectedSlot);
  if (
    x === null ||
    y === null ||
    z === null ||
    yaw === null ||
    pitch === null ||
    health === null ||
    health < 0 ||
    health > MAX_HEALTH ||
    air === null ||
    air < 0 ||
    air > MAX_AIR ||
    selectedSlot === null ||
    selectedSlot < 0 ||
    selectedSlot >= HOTBAR_SIZE ||
    typeof value.flying !== "boolean"
  ) {
    return null;
  }
  const player: SaveData["player"] = {
    x,
    y,
    z,
    yaw,
    pitch,
    health,
    air,
    flying: value.flying,
    selectedSlot,
  };

  if (typeof value.food === "number" && Number.isFinite(value.food)) {
    player.food = Math.max(0, Math.min(20, Math.round(value.food)));
  }
  if (typeof value.foodSaturation === "number" && Number.isFinite(value.foodSaturation)) {
    player.foodSaturation = Math.max(0, Math.min(20, value.foodSaturation));
  }
  if (typeof value.foodExhaustion === "number" && Number.isFinite(value.foodExhaustion)) {
    player.foodExhaustion = Math.max(0, Math.min(40, value.foodExhaustion));
  }

  return player;
}

function parsePosition(value: unknown): { x: number; y: number; z: number } | null {
  if (!isRecord(value)) return null;
  const x = finite(value.x);
  const y = finite(value.y);
  const z = finite(value.z);
  return x === null || y === null || z === null ? null : { x, y, z };
}

function parseInventory(value: unknown): SavedInventory | null {
  if (!isRecord(value) || !Array.isArray(value.slots) || !Array.isArray(value.overflow)) {
    return null;
  }
  const slots: [number, ItemStack][] = [];
  const seen = new Set<number>();
  for (const entry of value.slots) {
    if (!Array.isArray(entry) || entry.length !== 2) return null;
    const index = integer(entry[0]);
    const stack = parseStack(entry[1]);
    if (index === null || index < 0 || index >= INVENTORY_SIZE || seen.has(index) || !stack) {
      return null;
    }
    seen.add(index);
    slots.push([index, stack]);
  }

  const overflow: ItemStack[] = [];
  for (const entry of value.overflow) {
    const stack = parseStack(entry);
    if (!stack) return null;
    overflow.push(stack);
  }

  const armor: [number, ItemStack][] = [];
  if (Array.isArray(value.armor)) {
    const seenArmor = new Set<number>();
    for (const entry of value.armor) {
      if (!Array.isArray(entry) || entry.length !== 2) return null;
      const index = integer(entry[0]);
      const stack = parseStack(entry[1]);
      if (index === null || index < 0 || index >= ARMOR_SLOT_COUNT || seenArmor.has(index) || !stack) {
        return null;
      }
      seenArmor.add(index);
      armor.push([index, stack]);
    }
  }

  return value.armor !== undefined ? { slots, overflow, armor } : { slots, overflow };
}

function parseStack(value: unknown): ItemStack | null {
  if (!isRecord(value)) return null;
  const id = integer(value.id);
  const count = integer(value.count);
  if (id === null || count === null || !isItem(id) || count <= 0 || count > stackLimit(id)) {
    return null;
  }

  if (value.durability === undefined) return { id, count };
  const durability = integer(value.durability);
  const max = maxDurability(id);
  if (durability === null || max <= 0 || durability < 0 || durability > max) return null;
  return { id, count, durability };
}

function parseBlocks(value: unknown): number[] | null {
  if (!Array.isArray(value) || value.length % 4 !== 0) return null;
  const blocks: number[] = [];
  for (let i = 0; i < value.length; i += 4) {
    const x = integer(value[i]);
    const y = integer(value[i + 1]);
    const z = integer(value[i + 2]);
    const id = integer(value[i + 3]);
    if (
      x === null ||
      y === null ||
      z === null ||
      id === null ||
      Math.abs(x) > 2 ** 30 ||
      Math.abs(z) > 2 ** 30 ||
      y < 1 ||
      y >= 128 ||
      id < 0 ||
      id >= BLOCKS.length
    ) {
      return null;
    }
    blocks.push(x, y, z, id);
  }
  return blocks;
}

function parseChests(value: unknown): SavedChest[] | null {
  if (!Array.isArray(value) || value.length > 4096) return null;
  const chests: SavedChest[] = [];
  for (const entry of value) {
    if (!isRecord(entry)) return null;
    const x = integer(entry.x);
    const y = integer(entry.y);
    const z = integer(entry.z);
    const slots = parseContainerSlots(entry.slots, CHEST_SLOTS);
    if (x === null || y === null || z === null || !slots) return null;
    chests.push({ x, y, z, slots });
  }
  return chests;
}

function parseFurnaces(value: unknown): SavedFurnace[] | null {
  if (!Array.isArray(value) || value.length > 4096) return null;
  const furnaces: SavedFurnace[] = [];
  for (const entry of value) {
    if (!isRecord(entry)) return null;
    const x = integer(entry.x);
    const y = integer(entry.y);
    const z = integer(entry.z);
    const slots = parseContainerSlots(entry.slots, FURNACE_SLOTS);
    const progress = finite(entry.progress);
    const burnRemaining = finite(entry.burnRemaining);
    const burnTotal = finite(entry.burnTotal);
    if (
      x === null ||
      y === null ||
      z === null ||
      !slots ||
      progress === null ||
      progress < 0 ||
      progress > 1 ||
      burnRemaining === null ||
      burnRemaining < 0 ||
      burnTotal === null ||
      burnTotal < 0
    ) {
      return null;
    }
    furnaces.push({ x, y, z, slots, progress, burnRemaining, burnTotal });
  }
  return furnaces;
}

function parseAnimals(value: unknown): SavedAnimal[] | null {
  // A missing field is valid: saves written before 6C-A simply had no animals.
  if (value === undefined) return [];
  if (!Array.isArray(value) || value.length > MAX_ANIMALS) return null;

  const animals: SavedAnimal[] = [];
  for (const entry of value) {
    if (!isRecord(entry) || !isAnimalKind(entry.kind)) return null;
    const x = finite(entry.x);
    const y = finite(entry.y);
    const z = finite(entry.z);
    const health = finite(entry.health);
    const age = finite(entry.age);
    if (
      x === null ||
      y === null ||
      z === null ||
      y < 0 ||
      y > WORLD_HEIGHT + 16 ||
      health === null ||
      health <= 0 ||
      health > animalMaxHealth(entry.kind) ||
      age === null ||
      age < 0
    ) {
      return null;
    }
    animals.push({ kind: entry.kind, x, y, z, health, age });
  }
  return animals;
}

function parseContainerSlots(value: unknown, size: number): (ItemStack | null)[] | null {
  if (!Array.isArray(value) || value.length !== size) return null;
  const slots: (ItemStack | null)[] = [];
  for (const entry of value) {
    if (entry === null) {
      slots.push(null);
      continue;
    }
    const stack = parseStack(entry);
    if (!stack) return null;
    slots.push(stack);
  }
  return slots;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function finite(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function integer(value: unknown): number | null {
  return typeof value === "number" && Number.isSafeInteger(value) ? value : null;
}
